import { discoveryFilterStore } from "../store/filters";
import { yourInsightFilterStore } from "../store/yourInsightsFilter";
import type { DiscoveryFieldSelection } from "../types/filters/common";
import {
    YourInsightFilterContentSubType,
    YourInsightFilterContentType,
    type YourInsightsFilters,
} from "../types/filters/yourInsights";
import type { IHubContentSubType } from "../types/ihubContentType";
import type { IHubContentType } from "../types/ihubContentType";
import { TopNavBarTabs, TabQueryParamValue } from "../types/navigation";

import {
    IHubQueryParam,
    YourInsightsContentTypeParam,
    YourInsightsSubTypeParam,
} from "./permission.utils";
import { getValueFromSearchParams } from "./window.utils";

export const getYourInsightsSubType = ():
    | YourInsightFilterContentSubType
    | undefined => {
    const subType = getValueFromSearchParams(
        IHubQueryParam.YOUR_INSIGHT_SUB_TYPE,
    );
    switch (subType) {
        case YourInsightsSubTypeParam.WORKBENCH:
            return YourInsightFilterContentSubType.WORKBENCH_INSIGHTS;
        case YourInsightsSubTypeParam.PACKAGED:
            return YourInsightFilterContentSubType.PACKAGED_INSIGHTS;
        default:
            return undefined;
    }
};

export const getYourInsightsContentType = (): YourInsightFilterContentType => {
    const contentType = getValueFromSearchParams(
        IHubQueryParam.YOUR_INSIGHT_CONTENT_TYPE,
    );
    if (contentType === YourInsightsContentTypeParam.PITCHED_CONTENT) {
        return YourInsightFilterContentType.PITCHED_CONTENT;
    }
    return YourInsightFilterContentType.INSIGHTS;
};

export const getYourInsightsContentTypeQueryParam = (
    contentType: YourInsightFilterContentType,
): string => {
    return contentType === YourInsightFilterContentType.PITCHED_CONTENT
        ? YourInsightsContentTypeParam.PITCHED_CONTENT
        : YourInsightsContentTypeParam.INSIGHTS;
};

export const getYourInsightsSubTypeQueryParam = (
    subType: YourInsightFilterContentSubType,
): string => {
    return subType === YourInsightFilterContentSubType.WORKBENCH_INSIGHTS
        ? YourInsightsSubTypeParam.WORKBENCH
        : YourInsightsSubTypeParam.PACKAGED;
};

export const initialTab = (): TopNavBarTabs => {
    const tabFromQuery = getValueFromSearchParams(IHubQueryParam.INITIAL_TAB);

    if (tabFromQuery === TabQueryParamValue.GENERATED_INSIGHTS.toString()) {
        return TopNavBarTabs.YOUR_INSIGHT_DECKS;
    }

    if (tabFromQuery === TabQueryParamValue.EXPLORE_WITH_AI.toString()) {
        return TopNavBarTabs.EXPLORE_WITH_AI;
    }

    return TopNavBarTabs.ALL_INSIGHTS;
};

export const getTabQueryParam = (tab: TopNavBarTabs): TabQueryParamValue => {
    switch (tab) {
        case TopNavBarTabs.YOUR_INSIGHT_DECKS:
            return TabQueryParamValue.GENERATED_INSIGHTS;
        case TopNavBarTabs.EXPLORE_WITH_AI:
            return TabQueryParamValue.EXPLORE_WITH_AI;
        case TopNavBarTabs.ALL_INSIGHTS:
        default:
            return TabQueryParamValue.ALL_INSIGHTS;
    }
};

export const isAllInsightsTab = (tab: TopNavBarTabs): boolean =>
    tab === TopNavBarTabs.ALL_INSIGHTS;

export const isYourInisghtsTab = (tab: TopNavBarTabs): boolean =>
    tab === TopNavBarTabs.YOUR_INSIGHT_DECKS;

export const isExploreWithAITab = (tab: TopNavBarTabs): boolean =>
    tab === TopNavBarTabs.EXPLORE_WITH_AI;

export const getFilterStore = (
    navigationTab: TopNavBarTabs,
): {
    contentType: IHubContentType | YourInsightFilterContentType;
    contentSubType:
        | IHubContentSubType
        | YourInsightFilterContentSubType
        | undefined;
    fieldSelections: DiscoveryFieldSelection | YourInsightsFilters;
    searchQuery?: string;
} => {
    if (navigationTab === TopNavBarTabs.YOUR_INSIGHT_DECKS) {
        const { contentType, contentSubType, filters } =
            yourInsightFilterStore.getState();
        // TODO: Update fieldSelections when filters supported for your insights tab
        const fieldSelections = filters;

        return {
            contentType,
            contentSubType,
            fieldSelections,
        };
    } else {
        const { contentType } = discoveryFilterStore.getState();
        const { contentSubType, filters } =
            discoveryFilterStore.getState()[contentType];
        const fieldSelections =
            filters[contentSubType.provider].fieldSelections;
        return {
            contentType,
            contentSubType,
            fieldSelections,
            searchQuery:
                filters[contentSubType.provider]?.searchQuery?.trim() ??
                undefined,
        };
    }
};
